import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSelector from "./LanguageSelector";
import CurrencySwitcher from "./CurrencySwitcher";
import { trackSearchQuery } from "@/hooks/use-analytics";
import {
  Search,
  Menu,
  X,
  ChevronDown,
  ShoppingCart,
  User,
  Shield,
  LogOut,
  Settings,
  Mail,
  Phone,
} from "lucide-react";

const navItemClass =
  "px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors";
const navIdleClass = "text-foreground/80 hover:text-primary hover:bg-muted/60";
const navActiveClass = "text-primary bg-primary/10";

const renderNavClass = ({ isActive }: { isActive: boolean }) =>
  `${navItemClass} ${isActive ? navActiveClass : navIdleClass}`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { state, openCart } = useCart();
  const { t } = useLanguage();
  const { user, profile, isAdmin, signOut } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProductMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkClick = () => setIsMenuOpen(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      // result_count -1 = "not measured at submission time" — the catalog
      // page that lands on /products?search= records the actual result_count
      // separately so we can detect zero-result queries.
      trackSearchQuery(q, -1, "header");
      navigate(`/products?search=${encodeURIComponent(q)}`);
    } else {
      /* Empty submit used to silently do nothing — buyers thought the
         search button was broken. Ship them to the full catalog so
         they at least see something happen. */
      navigate('/products');
    }
  };

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = mobileSearchQuery.trim();
    if (q) {
      trackSearchQuery(q, -1, "header-mobile");
      navigate(`/products?search=${encodeURIComponent(q)}`);
      setIsMenuOpen(false);
    } else {
      navigate('/products');
      setIsMenuOpen(false);
    }
  };

  const handleRequestQuote = () => navigate("/contact");

  const cartCount = state.items.length;

  return (
    <header className="bg-background/95 backdrop-blur-md border-b border-border sticky top-0 z-50 shadow-card">
      <div className="container mx-auto px-4">
        {/* Top bar: contact + utilities */}
        <div className="hidden md:flex items-center justify-between py-1.5 text-xs text-muted-foreground border-b border-border/60">
          <div className="flex items-center gap-5">
            {/* Direct contact — email only. Voice channel is disabled
                site-wide so buyers funnel through /contact or email for
                a single inbox to monitor. WhatsApp surface was removed
                in an earlier pass; phone was removed here too. */}
            <a href="mailto:info@longshineboitech.com" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
              <Mail className="w-3 h-3" /> info@longshineboitech.com
            </a>
            <a href="mailto:sales@longshineboitech.com" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
              <Mail className="w-3 h-3" /> sales@longshineboitech.com
            </a>
          </div>
          <div className="flex items-center gap-3">
            <CurrencySwitcher />
            <LanguageSelector />
            {user ? (
              <div className="flex items-center gap-3">
                {/* Identity. For admins the name itself links to /admin so we
                    don't repeat the word "Admin" three times (name + role
                    label + dedicated link). The Shield icon already signals
                    the admin role visually. */}
                {isAdmin ? (
                  <Link
                    to="/admin"
                    className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
                    title="Open admin dashboard"
                  >
                    <Shield className="w-3 h-3 text-primary" />
                    <span className="font-medium text-foreground">
                      {profile?.full_name || user.email?.split("@")[0]}
                    </span>
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium text-foreground">
                      {profile?.full_name || user.email?.split("@")[0]}
                    </span>
                  </span>
                )}
                <button onClick={signOut} className="hover:text-primary transition-colors inline-flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              </div>
            ) : (
              <Link to="/auth" className="hover:text-primary transition-colors inline-flex items-center gap-1">
                <User className="w-3 h-3" /> Sign in
              </Link>
            )}
          </div>
        </div>

        {/* Main navigation */}
        <div className="flex items-center gap-4 py-3">
          {/* Logo — hexagon mark + wordmark. The mark uses currentColor so it
              inherits the link's hover state without a separate hover ruleset. */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group text-primary">
            <img
              src="/logo.svg"
              alt="Longshine Boi-Tech"
              width="40"
              height="40"
              className="w-10 h-10 group-hover:scale-105 transition-smooth"
            />
            <div className="leading-tight">
              <div className="font-bold text-base sm:text-lg text-foreground group-hover:text-primary transition-fast tracking-tight">
                Longshine <span className="font-medium">Boi-Tech</span>
              </div>
              <div className="hidden sm:block text-[11px] text-muted-foreground tracking-wide">
                ISO 9001:2015 · WHO-GMP Certified
              </div>
            </div>
          </Link>

          {/* Desktop Navigation — kicks in at xl so it doesn't squeeze at 1024px */}
          <nav className="hidden xl:flex items-center gap-1 mx-auto">
            <NavLink to="/" end className={renderNavClass}>
              {t("nav.home")}
            </NavLink>
            <NavLink to="/about" className={renderNavClass}>
              {t("nav.about")}
            </NavLink>

            {/* Products dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setIsProductMenuOpen(true)}
              onMouseLeave={() => setIsProductMenuOpen(false)}
            >
              <NavLink
                to="/products"
                className={({ isActive }) =>
                  `${navItemClass} inline-flex items-center ${isActive ? navActiveClass : navIdleClass}`
                }
                onClick={() => setIsProductMenuOpen(false)}
              >
                {t("nav.products")}
                <ChevronDown
                  className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${
                    isProductMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </NavLink>

              {isProductMenuOpen && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  {/* Two-column dropdown — all 8 categories shown so every page
                      view passes link equity to every category landing page.
                      Pre-audit only 3 of 8 were listed which left 5 categories
                      with zero sitewide internal links. */}
                  <div className="w-[34rem] bg-popover border border-border rounded-lg shadow-elevated p-3 animate-in slide-in-from-top-2 duration-200 grid grid-cols-2 gap-x-3 gap-y-1">
                    <Link to="/products/human-apis" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Human APIs</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Active pharmaceutical ingredients</div>
                    </Link>
                    <Link to="/products/intermediates" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Intermediates</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Chemical building blocks</div>
                    </Link>
                    <Link to="/products/veterinary" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Veterinary APIs</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Compounds for animal health</div>
                    </Link>
                    <Link to="/products/peptides" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Peptides</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Custom peptide synthesis</div>
                    </Link>
                    <Link to="/products/excipients" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Excipients</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Pharmaceutical excipients</div>
                    </Link>
                    <Link to="/products/amino-acids" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Amino Acids</div>
                      <div className="text-xs text-muted-foreground mt-0.5">L-form amino acids</div>
                    </Link>
                    <Link to="/products/nootropics" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Nootropics</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Cognitive enhancement compounds</div>
                    </Link>
                    <Link to="/products/research-chemicals" className="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors">
                      <div className="text-sm font-medium text-foreground">Research Chemicals</div>
                      <div className="text-xs text-muted-foreground mt-0.5">For lab and research use</div>
                    </Link>
                    {/* Therapeutic-area shortcuts — captures long-tail
                        "diabetes API supplier"-style searches that were
                        previously only reachable via footer. Conversion
                        audit flagged this as a top-5 navigation gap. */}
                    <div className="col-span-2 border-t border-border mt-1 pt-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 mb-1">By therapeutic area</div>
                      <div className="grid grid-cols-3 gap-x-2">
                        <Link to="/therapeutic/antiviral" className="block px-3 py-1.5 text-xs text-muted-foreground rounded-md hover:bg-muted/60 hover:text-primary transition-colors">Antiviral</Link>
                        <Link to="/therapeutic/antibiotic" className="block px-3 py-1.5 text-xs text-muted-foreground rounded-md hover:bg-muted/60 hover:text-primary transition-colors">Antibiotic</Link>
                        <Link to="/therapeutic/cardiovascular" className="block px-3 py-1.5 text-xs text-muted-foreground rounded-md hover:bg-muted/60 hover:text-primary transition-colors">Cardiovascular</Link>
                        <Link to="/therapeutic/diabetes" className="block px-3 py-1.5 text-xs text-muted-foreground rounded-md hover:bg-muted/60 hover:text-primary transition-colors">Diabetes</Link>
                        <Link to="/therapeutic/oncology" className="block px-3 py-1.5 text-xs text-muted-foreground rounded-md hover:bg-muted/60 hover:text-primary transition-colors">Oncology</Link>
                        <Link to="/therapeutic/cns" className="block px-3 py-1.5 text-xs text-muted-foreground rounded-md hover:bg-muted/60 hover:text-primary transition-colors">CNS / Neurology</Link>
                      </div>
                    </div>
                    <div className="col-span-2 border-t border-border mt-1 pt-1 grid grid-cols-2 gap-2">
                      <Link to="/products" className="flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-primary hover:bg-primary/5 transition-colors">
                        View full catalogue <span aria-hidden>→</span>
                      </Link>
                      <Link to="/markets" className="flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-primary hover:bg-primary/5 transition-colors">
                        Markets we serve <span aria-hidden>→</span>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/capabilities" className={renderNavClass}>
              {t("nav.capabilities")}
            </NavLink>
            <NavLink to="/quality" className={renderNavClass}>
              {t("nav.quality")}
            </NavLink>
            <NavLink to="/news" className={renderNavClass}>
              {t("nav.news")}
            </NavLink>
            <NavLink to="/contact" className={renderNavClass}>
              {t("nav.contact")}
            </NavLink>
          </nav>

          {/* Right cluster: search + cart + CTA */}
          <div className="flex items-center gap-2 ml-auto xl:ml-0 shrink-0">
            {/* xl: search icon → /products. 2xl+: full input. */}
            <Link
              to="/products"
              className="hidden xl:inline-flex 2xl:hidden items-center justify-center w-9 h-9 rounded-md border border-input text-foreground hover:text-primary hover:bg-muted/60 transition-colors"
              aria-label="Search products"
            >
              <Search className="w-4 h-4" />
            </Link>
            <form onSubmit={handleSearch} className="relative group hidden 2xl:block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("search.placeholder")}
                className="pl-9 pr-3 py-2 w-56 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent focus:w-72 transition-all"
                aria-label="Search products"
              />
            </form>

            <button
              onClick={openCart}
              className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-input text-foreground hover:text-primary hover:bg-muted/60 transition-colors"
              aria-label={`Cart (${cartCount} items)`}
            >
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center p-0 text-[10px]"
                >
                  {cartCount}
                </Badge>
              )}
            </button>

            <Button
              size="sm"
              className="hidden sm:inline-flex bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary shadow-professional hover:shadow-elevated transition-smooth"
              onClick={handleRequestQuote}
            >
              {t("button.request_quote")}
            </Button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-muted/60 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="xl:hidden py-4 border-t border-border animate-in slide-in-from-top-2 duration-200">
            <nav className="space-y-1">
              <form onSubmit={handleMobileSearch} className="relative mb-3 group">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
                <input
                  type="text"
                  value={mobileSearchQuery}
                  onChange={(e) => setMobileSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="pl-10 pr-4 py-2 w-full bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast"
                />
              </form>

              <NavLink to="/" end onClick={handleLinkClick} className={renderNavClass + " block"}>
                {t("nav.home")}
              </NavLink>
              <NavLink to="/about" onClick={handleLinkClick} className={renderNavClass + " block"}>
                {t("nav.about")}
              </NavLink>

              <div>
                <NavLink to="/products" end onClick={handleLinkClick} className={renderNavClass + " block"}>
                  {t("nav.products")}
                </NavLink>
                <div className="pl-4 mt-1 space-y-1">
                  <Link to="/products/human-apis" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Human APIs</Link>
                  <Link to="/products/intermediates" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Intermediates</Link>
                  <Link to="/products/veterinary" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Veterinary APIs</Link>
                  <Link to="/products/peptides" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Peptides</Link>
                  <Link to="/products/excipients" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Excipients</Link>
                  <Link to="/products/amino-acids" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Amino Acids</Link>
                  <Link to="/products/nootropics" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Nootropics</Link>
                  <Link to="/products/research-chemicals" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-muted-foreground rounded-md hover:text-primary hover:bg-muted/60 transition-colors">Research Chemicals</Link>
                  <Link to="/markets" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-primary font-medium rounded-md hover:bg-muted/60 transition-colors">Markets we serve →</Link>
                  <Link to="/certifications" onClick={handleLinkClick} className="block px-3 py-2 text-sm text-primary font-medium rounded-md hover:bg-muted/60 transition-colors">Certifications →</Link>
                </div>
              </div>

              <NavLink to="/capabilities" onClick={handleLinkClick} className={renderNavClass + " block"}>
                {t("nav.capabilities")}
              </NavLink>
              <NavLink to="/quality" onClick={handleLinkClick} className={renderNavClass + " block"}>
                {t("nav.quality")}
              </NavLink>
              <NavLink to="/news" onClick={handleLinkClick} className={renderNavClass + " block"}>
                {t("nav.news")}
              </NavLink>
              <NavLink to="/contact" onClick={handleLinkClick} className={renderNavClass + " block"}>
                {t("nav.contact")}
              </NavLink>

              {user ? (
                <div className="border-t pt-3 mt-3 space-y-2">
                  <div className="flex items-center gap-3 px-3">
                    <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">
                        {profile?.full_name || user.email?.split("@")[0]}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        {isAdmin && <Shield className="w-3 h-3" />}
                        <span>{isAdmin ? "Admin" : "User"}</span>
                      </div>
                    </div>
                  </div>

                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-muted/60 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  )}

                  <button
                    onClick={() => {
                      signOut();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors w-full"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <div className="border-t pt-3 mt-3">
                  <Link
                    to="/auth"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-muted/60 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>Sign In</span>
                  </Link>
                </div>
              )}

              <div className="pt-3">
                <Button
                  className="w-full bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary shadow-professional"
                  onClick={handleRequestQuote}
                >
                  {t("button.request_quote")}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
